<script setup lang="ts">
import { X as XIcon } from '@lucide/vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import CreateCollectionIcon from '@/components/icons/CreateCollectionIcon.vue'
import AddToCollectionPickerContent from '@/components/map/AddToCollectionPickerContent.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import {
  ADD_TO_COLLECTION_REQUEST_EVENT,
  addPayloadToCollectionWithToasts,
  createCollectionForAddTarget,
  isAddToCollectionPickerOpen,
  type AddToCollectionPayload,
  type AddToCollectionTarget,
} from '@/services/addToCollectionService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { showError } from '@/services/toastService'
import { isSmallScreen } from '@/services/uiService'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'

defineOptions({ name: 'AddToCollectionHost' })

const searchWorkspaceStore = useSearchWorkspaceStore()
const open = ref(false)
const loading = ref(false)
const submitting = ref(false)
const addingWorkspaceId = ref<string | null>(null)
const payload = ref<AddToCollectionPayload | null>(null)
const creating = ref(false)
const draftName = ref('New Collection')

const editableWorkspaces = computed<AddToCollectionTarget[]>(() =>
  searchWorkspaceStore.allWorkspaces
    .filter((workspace) => {
      if (!(workspace.isOwner || workspace.canEdit === true)) return false
      if (
        payload.value?.sourceWorkspaceId &&
        workspace.clientId === payload.value.sourceWorkspaceId
      ) {
        return false
      }
      const record = workspace as typeof workspace & {
        isSystem?: boolean
        systemKey?: string | null
      }
      if (record.isSystem || record.systemKey === 'pinned') return false
      return true
    })
    .map((workspace) => ({
      clientId: workspace.clientId,
      name: workspace.name,
      isPublic: workspace.isPublic,
      isOwner: workspace.isOwner,
      canEdit: workspace.canEdit,
      canView: workspace.canView,
      isBookmarked: workspace.isBookmarked,
      ownerNames: workspace.ownerNames,
      userRole: workspace.userRole,
      coverPhotoThumbnailUrl: workspace.coverPhotoThumbnailUrl,
    })),
)

const resetCreateState = () => {
  creating.value = false
  draftName.value = 'New Collection'
}

const close = (force = false) => {
  if (submitting.value && !force) return
  open.value = false
  isAddToCollectionPickerOpen.value = false
  payload.value = null
  addingWorkspaceId.value = null
  resetCreateState()
}

const requestClose = () => close()

const loadCollections = async () => {
  loading.value = true
  try {
    await searchWorkspaceStore.ensureWorkspacesLoaded(true)
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to load collections for Add to collection', {
      error,
    })
    showError(
      `Failed to load collections: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    loading.value = false
  }
}

const openForPayload = async (nextPayload: AddToCollectionPayload) => {
  payload.value = nextPayload
  resetCreateState()
  open.value = true
  isAddToCollectionPickerOpen.value = true
  await loadCollections()
}

const handleRequest = (event: Event) => {
  const detail = (event as CustomEvent<AddToCollectionPayload>).detail
  if (!detail) return
  void openForPayload(detail)
}

const selectWorkspace = async (workspace: AddToCollectionTarget) => {
  if (!payload.value || submitting.value) return
  submitting.value = true
  addingWorkspaceId.value = workspace.clientId
  try {
    await addPayloadToCollectionWithToasts(payload.value, workspace)
    close(true)
  } catch {
    // Error toast/logging is handled by the service. Keep picker open for retry.
  } finally {
    submitting.value = false
    addingWorkspaceId.value = null
  }
}

const startCreate = () => {
  creating.value = true
  draftName.value = 'New Collection'
}

const createAndAdd = async () => {
  if (!payload.value || submitting.value) return
  submitting.value = true
  let workspace: AddToCollectionTarget | null = null
  try {
    workspace = await createCollectionForAddTarget(draftName.value)
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to create collection from Add to collection', {
      error,
    })
    showError(
      `Failed to create collection: ${error instanceof Error ? error.message : String(error)}`,
    )
    submitting.value = false
    return
  }

  try {
    await addPayloadToCollectionWithToasts(payload.value, workspace)
    close(true)
  } catch {
    // The collection was created and remains in the catalog. Add failure is reported by the service.
  } finally {
    submitting.value = false
  }
}

const handleHeaderAction = () => {
  if (creating.value) {
    void createAndAdd()
    return
  }
  startCreate()
}

onMounted(() => {
  window.addEventListener(ADD_TO_COLLECTION_REQUEST_EVENT, handleRequest)
})

onUnmounted(() => {
  window.removeEventListener(ADD_TO_COLLECTION_REQUEST_EVENT, handleRequest)
  isAddToCollectionPickerOpen.value = false
})
</script>

<template>
  <MobileActionSheet v-if="isSmallScreen" :open="open" title="Add to Collection" @close="close">
    <template #header-left>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
        aria-label="Close Add to Collection"
        @click="requestClose"
      >
        <XIcon class="h-4 w-4" />
      </button>
    </template>
    <template #header-right>
      <button
        type="button"
        class="flex h-9 items-center justify-center rounded-full border border-emerald-200 bg-white px-4 text-base font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
        :disabled="submitting || loading"
        :aria-label="creating ? 'Create collection' : 'Create new collection'"
        @click="handleHeaderAction"
      >
        <span v-if="creating">Create</span>
        <CreateCollectionIcon v-else class="h-4 w-4" />
      </button>
    </template>

    <div v-if="loading" class="px-4 py-6 text-center text-sm text-gray-500">
      Loading collections...
    </div>
    <AddToCollectionPickerContent
      v-else
      :workspaces="editableWorkspaces"
      :adding-workspace-id="addingWorkspaceId"
      :creating="creating"
      :draft-name="draftName"
      @update:draft-name="draftName = $event"
      @select="selectWorkspace"
    />
  </MobileActionSheet>

  <BaseModal
    v-else
    :is-open="open"
    title="Add to Collection"
    size="lg"
    content-class="rounded-2xl overflow-hidden"
    body-class="p-0"
    :show-header="false"
    @close="requestClose"
  >
    <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
        aria-label="Close Add to Collection"
        @click="requestClose"
      >
        <XIcon class="h-4 w-4" />
      </button>
      <h2 class="text-sm font-semibold text-gray-900">Add to Collection</h2>
      <button
        type="button"
        class="flex h-9 items-center justify-center rounded-full border border-emerald-200 bg-white px-4 text-base font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
        :disabled="submitting || loading"
        :aria-label="creating ? 'Create collection' : 'Create new collection'"
        @click="handleHeaderAction"
      >
        <span v-if="creating">Create</span>
        <CreateCollectionIcon v-else class="h-4 w-4" />
      </button>
    </div>
    <div v-if="loading" class="px-4 py-6 text-center text-sm text-gray-500">
      Loading collections...
    </div>
    <AddToCollectionPickerContent
      v-else
      class="max-h-[60vh]"
      :workspaces="editableWorkspaces"
      :adding-workspace-id="addingWorkspaceId"
      :creating="creating"
      :draft-name="draftName"
      @update:draft-name="draftName = $event"
      @select="selectWorkspace"
    />
  </BaseModal>
</template>
